import { useId, useMemo, useState } from 'react';
import type { TypeCatalogEntry } from './api';
import type { Lang } from './i18n';
import type { Translate } from './i18n';

function labelFor(entry: TypeCatalogEntry, lang: Lang): string {
  return lang === 'sv'
    ? entry.labelSv || entry.labelEn || entry.value
    : entry.labelEn || entry.labelSv || entry.value;
}

/**
 * Free-text type input with source-labelled suggestions. This is intentionally
 * a combobox, not a select: an unknown value from another app must remain valid.
 */
export function TypeField({
  value,
  onChange,
  options,
  lang,
  t,
}: {
  value: string;
  onChange: (value: string) => void;
  options: TypeCatalogEntry[];
  lang: Lang;
  t: Translate;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  const query = value.trim().toLocaleLowerCase();
  const filtered = useMemo(() => {
    if (!query) return options;
    return options.filter((entry) =>
      [entry.value, entry.source, labelFor(entry, lang)]
        .join(' ')
        .toLocaleLowerCase()
        .includes(query),
    );
  }, [lang, options, query]);

  const grouped = useMemo(() => {
    const groups = new Map<string, TypeCatalogEntry[]>();
    for (const entry of filtered) {
      const list = groups.get(entry.source) ?? [];
      list.push(entry);
      groups.set(entry.source, list);
    }
    return [...groups.entries()];
  }, [filtered]);

  const guideGroups = useMemo(() => {
    const groups = new Map<string, TypeCatalogEntry[]>();
    for (const entry of options) {
      const list = groups.get(entry.source) ?? [];
      list.push(entry);
      groups.set(entry.source, list);
    }
    return [...groups.entries()];
  }, [options]);

  return (
    <div className="ui-field">
      <label className="ui-field-label" htmlFor={`${listId}-input`}>
        {t('edit.type')}
      </label>
      <div
        className="type-combobox"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
        }}
      >
        <input
          id={`${listId}-input`}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setOpen(false);
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
            }
          }}
        />
        {open && (
          <div className="type-suggestions" id={listId} role="listbox">
            {grouped.length === 0 && <p className="muted">{t('type.noMatches')}</p>}
            {grouped.map(([source, entries]) => (
              <div key={source} className="type-source-group">
                <div className="type-source-heading">{source}</div>
                {entries.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    role="option"
                    aria-selected={entry.value === value}
                    className="type-option"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onChange(entry.value);
                      setOpen(false);
                    }}
                  >
                    <span className="type-value">{entry.value}</span>
                    <span className="type-source">({entry.source})</span>
                    {labelFor(entry, lang) !== entry.value && (
                      <span className="type-label">{labelFor(entry, lang)}</span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="type-field-footer">
        <span className="ui-field-hint">{t('type.freeTextHint')}</span>
        <button type="button" className="link" onClick={() => setGuideOpen(true)}>
          {t('type.known')}
        </button>
      </div>

      {guideOpen && (
        <div className="modal-backdrop" role="presentation" onClick={() => setGuideOpen(false)}>
          <div
            className="modal type-guide"
            role="dialog"
            aria-modal="true"
            aria-label={t('type.guideTitle')}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-head">
              <strong>{t('type.guideTitle')}</strong>
              <button className="link" type="button" onClick={() => setGuideOpen(false)}>
                {t('actions.dismiss')}
              </button>
            </div>
            <p className="muted">{t('type.guideHint')}</p>
            <div className="type-guide-list">
              {guideGroups.map(([source, entries]) => (
                <section key={source}>
                  <h3>{source}</h3>
                  <ul>
                    {entries.map((entry) => (
                      <li key={entry.id}>
                        <button
                          type="button"
                          className="link"
                          onClick={() => {
                            onChange(entry.value);
                            setGuideOpen(false);
                          }}
                        >
                          <span className="type-value">{entry.value}</span>
                          <span className="type-label">{labelFor(entry, lang)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
